import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Plus } from '../../assets/icons/plus.js';
import CustomTable from '../../components/common/CustomTable/index.jsx';
import { useSelection } from '../../hooks/use-selection.js';
import { getColumns } from './columns.js';
import { styles } from './styles.js';
import { onPageChange, onPageSizeChange, onSortChange } from '../../store/slices/tableController.js';
import PermissionChecker from '../../components/common/PermissionChecker/index.js';
import {
    useGetChangeRequestPoliciesMutation,
    useSearchChangeRequestPoliciesMutation,
} from '../../api/changeRequestPolicies';

const ChangeRequestPolicies = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.session);
    const { enqueueSnackbar } = useSnackbar();
    const controller = useSelector((state) => state.tableController);
    const [viewMappedPositionsData, setViewMappedPositionsData] = useReducer(
        (state, action) => ({
            ...state,
            ...action,
        }),
        {
            show: false,
            id: '',
        },
    );

    const [crPoliciesData, setCrPoliciesData] = useState();

    const [searchChangeRequestPolicies, { isLoading: isSearchLoading }] = useSearchChangeRequestPoliciesMutation();
    const [getChangeRequestPolicies, { isLoading: isGetLoading }] = useGetChangeRequestPoliciesMutation();

    const isLoading = isSearchLoading || isGetLoading;

    const [selectedPositions, handleSelect, handleSelectAll] = useSelection(crPoliciesData?.changeRequestPolicies);

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                if (searched) {
                    const query = new URLSearchParams({});
                    if (filters.length) {
                        query.append('c', filters[0].property);
                        query.append('q', filters[0].value);
                    }
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);

                    const response = await searchChangeRequestPolicies(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setCrPoliciesData(response);
                } else {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);

                    const response = await getChangeRequestPolicies(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setCrPoliciesData(response);
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchChangeRequestPolicies],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (row) => {
        navigate(`/changerequest/crpolicies/view/${row}`);
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Change Policy</Typography>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_CREATE_CHANGE_REQUEST_POLICY']}
                    noAuthRedirect={false}
                >
                    <Button
                        color="primary"
                        onClick={() => navigate('/changerequest/crpolicies/add')}
                        size="large"
                        startIcon={<Plus fontSize="small" />}
                        variant="contained"
                    >
                        Add
                    </Button>
                </PermissionChecker>
            </Box>
            <Card sx={styles.contentBodyWrapper}>
                <CardContent>
                    <CustomTable
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSelect={handleSelect}
                        onSelectAll={handleSelectAll}
                        onSortChange={handleSortChange}
                        page={controller.page + 1}
                        columns={getColumns(user)}
                        rows={crPoliciesData?.changeRequestPolicies || []}
                        rowsCount={crPoliciesData?.changeRequestPolicyCount || 0}
                        selectedRows={selectedPositions}
                        sort={controller.sort}
                        sortBy={controller.sortBy}
                        showRowSelector={false}
                        showPagignation
                        keyColumn="id"
                        route="changerequest/crpolicies"
                        rowId={viewMappedPositionsData.id}
                        action={() =>
                            fetchData(
                                controller.searched,
                                controller.filters,
                                controller.sort,
                                controller.sortBy,
                                controller.page,
                                controller.pageSize,
                            )
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default ChangeRequestPolicies;
